import { http, HttpResponse } from "msw";
import { generateMockJwt, getState, updateState } from "../state";

/**
 * Auth + health + first-user handlers.
 *
 * Per the plan: MSW intercepts login/session endpoints and always returns
 * "yes, you're logged in" once a callsign has been bound. The "ADMIN" code
 * is a hard-coded backdoor for the demo so reviewers can flip into admin
 * mode without seeded credentials.
 */
export const authHandlers = [
  http.get("/api/v1/healthcheck", () => {
    return HttpResponse.json({
      dns: "demo.dev.pvarki.fi",
      version: "mock-2.0.0",
      deployment: "MOCK DEMO",
    });
  }),

  http.get("/api/v1/healthcheck/services", async () => {
    // Reflect whichever integrations have actually been built into
    // public/ui/* — we read the same manifest that the federation runtime
    // uses, so this stays consistent with the products endpoint.
    try {
      const resp = await fetch("/product-integrations.json");
      if (resp.ok) {
        const integrations = await resp.json();
        const products: Record<string, boolean> = {};
        const list = Array.isArray(integrations)
          ? integrations
          : integrations.products;
        if (Array.isArray(list)) {
          for (const p of list) {
            if (p.shortname) products[p.shortname] = true;
          }
        }
        return HttpResponse.json({
          all_ok: Object.keys(products).length > 0,
          products,
        });
      }
    } catch {
      // fall through
    }
    return HttpResponse.json({ all_ok: false, products: {} });
  }),

  http.get("/api/v1/check-auth/mtls_or_jwt", () => {
    const state = getState();
    if (!state.isAuthenticated || !state.currentCallsign) {
      return new HttpResponse(null, { status: 403 });
    }
    return HttpResponse.json({
      type: "jwt",
      userid: state.currentCallsign,
      payload: { CN: state.currentCallsign },
    });
  }),

  http.get("/api/v1/check-auth/validuser", () => {
    const state = getState();
    if (!state.isAuthenticated || !state.currentCallsign) {
      return new HttpResponse(null, { status: 403 });
    }
    return HttpResponse.json({
      isValidUser: true,
      userid: state.currentCallsign,
    });
  }),

  http.get("/api/v1/check-auth/validuser/admin", () => {
    const state = getState();
    if (state.currentRole === "admin") {
      return HttpResponse.json({ isAdmin: true });
    }
    return new HttpResponse(null, { status: 403 });
  }),

  http.get("/api/v1/firstuser/check-code", ({ request }) => {
    const url = new URL(request.url);
    const code = url.searchParams.get("temp_admin_code") || "";
    const isAdmin =
      code.toUpperCase() === "ADMIN" || code.toUpperCase().startsWith("ADMIN-");
    return HttpResponse.json({ code_ok: isAdmin });
  }),

  http.post("/api/v1/token/code/exchange", async ({ request }) => {
    const body = (await request.json()) as { code: string };
    const jwt = generateMockJwt("pending-admin");
    return HttpResponse.json({ jwt: body.code ? jwt : "" });
  }),

  http.post("/api/v1/firstuser/add-admin", async ({ request }) => {
    const body = (await request.json()) as { callsign: string };
    const state = getState();
    const jwt = generateMockJwt(body.callsign);

    if (!state.users.find((u) => u.callsign === body.callsign)) {
      state.users.push({
        callsign: body.callsign,
        roles: ["admin"],
        extre: null,
      });
    }
    if (!state.enrollments.find((e) => e.callsign === body.callsign)) {
      state.enrollments.push({
        callsign: body.callsign,
        approvecode: "MOCK-" + body.callsign.slice(0, 4).toUpperCase(),
        state: 1,
      });
    }

    updateState({
      currentCallsign: body.callsign,
      currentRole: "admin",
      isAuthenticated: true,
      jwt,
      users: state.users,
      enrollments: state.enrollments,
    });

    localStorage.setItem("token", jwt);
    localStorage.setItem("callsign", body.callsign);

    return HttpResponse.json({
      admin_added: true,
      jwt_exchange_code: "mock-exchange-code",
    });
  }),
];
